import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import {
	ArrowDownToLine,
	CircleX,
	FileCheck2,
	Loader2Icon,
	Lock,
	TriangleAlert,
} from 'lucide-react';
import ComponentContainer from '@/components/component-container/ComponentContainer';
import { Button } from '@/components/ui/button';
import {
	DocumentLineageInfo,
	DocumentLineageSignature,
} from '@/entities/document-lineage';
import { getDocumentLineageInfo } from '@/use-cases/get-document-lineage-info';
import styles from './document-lineage.module.scss';

type LineageViewState =
	| { status: 'loading' }
	| { status: 'ok'; info: DocumentLineageInfo }
	| { status: 'not-found' }
	| { status: 'error' };

// The presigned download URL lives 5 minutes. Anything older than this is
// refreshed before it is opened, rather than handing the visitor a link that
// has already expired.
const DOWNLOAD_URL_MAX_AGE_MS = 4 * 60 * 1000;

function formatDateTime(isoDate: string): string {
	const parsed = DateTime.fromISO(isoDate);
	return parsed.isValid ? parsed.toFormat('dd/MM/yyyy HH:mm ZZ') : isoDate;
}

const Field = ({ name, value }: { name: string; value: string }) => (
	<>
		<div className={styles['field-name']}>{name}</div>
		<div className={styles['field-value']}>{value}</div>
	</>
);

const Signature = ({ signature }: { signature: DocumentLineageSignature }) => (
	<div className={styles['signature']}>
		<div className={styles['signer-name']}>{signature.name}</div>
		<div className={styles['signature-detail']}>
			Certificado: {signature.cert_serial}
		</div>
		<div className={styles['signature-detail']}>
			Firmado: {formatDateTime(signature.signed_at)}
		</div>
	</div>
);

type DownloadProps = {
	isRefreshing: boolean;
	hasFailed: boolean;
	onDownload: () => void;
};

// Path 2. Rendered only when the server says the owner allows it and handed us
// a presigned URL; the URL itself is never written into the page text.
const DownloadSection = ({
	isRefreshing,
	hasFailed,
	onDownload,
}: DownloadProps) => (
	<div className={styles['download']} data-testid="lineage-download">
		<Button onClick={onDownload} disabled={isRefreshing}>
			{isRefreshing ? (
				<Loader2Icon className="animate-spin" />
			) : (
				<ArrowDownToLine />
			)}
			Descargar documento firmado
		</Button>
		{hasFailed ? (
			<p className={styles['download-note']}>
				No fue posible preparar la descarga. Intenta de nuevo.
			</p>
		) : (
			<p className={styles['download-note']}>
				El propietario habilitó la descarga pública de este documento.
			</p>
		)}
	</div>
);

const WithdrawnNotice = () => (
	<div className={styles['withdrawn']} data-testid="lineage-withdrawn">
		<Lock size={18} />
		<p>El propietario retiró la descarga pública de este documento.</p>
		<p className={styles['download-note']}>
			El registro de trazabilidad sigue siendo válido; solo el archivo dejó
			de estar disponible.
		</p>
	</div>
);

const LineageCard = ({
	info,
	download,
}: {
	info: DocumentLineageInfo;
	download: DownloadProps;
}) => (
	<>
		<div className={styles['title']}>
			<div className={styles['text']}>{info.document_name}</div>
			<div className={styles['subtitle']}>
				Registro de trazabilidad de Cybersign
			</div>
		</div>

		{info.state === 'withdrawn' && <WithdrawnNotice />}
		{info.state === 'ok' && info.public_retrieval && info.download_url && (
			<DownloadSection {...download} />
		)}

		<div className={styles['content']}>
			<Field name="Páginas:" value={String(info.page_count)} />
			<Field
				name="Página de trazabilidad agregada:"
				value={formatDateTime(info.lineage_added_at)}
			/>
			<Field
				name="SHA-256 del documento original:"
				value={info.sha256_original}
			/>
			<Field
				name="SHA-256 del documento firmado:"
				value={info.sha256_final}
			/>
		</div>

		<div className={styles['signatures']}>
			<div className={styles['section-name']}>
				{info.signatures.length === 1 ? 'Firma' : 'Firmas'} (
				{info.signatures.length})
			</div>
			{info.signatures.map((signature) => (
				<Signature
					key={`${signature.cert_serial}-${signature.signed_at}`}
					signature={signature}
				/>
			))}
		</div>
	</>
);

const DocumentLineage = () => {
	const { ulid } = useParams<{ ulid: string }>();
	const { search } = useLocation();
	const [viewState, setViewState] = useState<LineageViewState>({
		status: 'loading',
	});
	const [isRefreshingDownload, setIsRefreshingDownload] = useState(false);
	const [hasDownloadFailed, setHasDownloadFailed] = useState(false);
	// When the presigned URL currently in state was issued.
	const downloadUrlFetchedAt = useRef(0);

	// The access token never leaves this module: it is not logged, not
	// reported to Honeybadger and not sent to analytics.
	const token = new URLSearchParams(search).get('k');

	useEffect(() => {
		if (!ulid || !token) {
			setViewState({ status: 'not-found' });
			return;
		}

		let isCurrent = true;
		setViewState({ status: 'loading' });

		getDocumentLineageInfo(ulid, token).then((result) => {
			if (!isCurrent) return;
			downloadUrlFetchedAt.current = Date.now();
			setViewState(
				result.status === 'ok'
					? { status: 'ok', info: result.info }
					: { status: result.status },
			);
		});

		return () => {
			isCurrent = false;
		};
	}, [ulid, token]);

	const handleDownload = useCallback(() => {
		if (viewState.status !== 'ok' || !viewState.info.download_url) {
			return;
		}

		setHasDownloadFailed(false);
		const isFresh =
			Date.now() - downloadUrlFetchedAt.current < DOWNLOAD_URL_MAX_AGE_MS;

		if (isFresh) {
			window.open(viewState.info.download_url, '_blank', 'noopener');
			return;
		}

		// Stale: the URL has probably expired. Open the tab synchronously so
		// the popup blocker lets it through, then point it at a fresh URL.
		const pending = window.open('', '_blank', 'noopener');
		setIsRefreshingDownload(true);

		getDocumentLineageInfo(ulid, token ?? '')
			.then((result) => {
				const freshUrl =
					result.status === 'ok' ? result.info.download_url : null;

				if (result.status === 'ok') {
					downloadUrlFetchedAt.current = Date.now();
					setViewState({ status: 'ok', info: result.info });
				}

				if (freshUrl && pending) {
					pending.location.href = freshUrl;
					return;
				}

				pending?.close();
				setHasDownloadFailed(true);
			})
			.finally(() => setIsRefreshingDownload(false));
	}, [ulid, token, viewState]);

	let content;

	switch (viewState.status) {
		case 'loading':
			content = (
				<div className={styles['message']}>
					<Loader2Icon className="animate-spin" size={32} />
					<p>Consultando el registro del documento…</p>
				</div>
			);
			break;
		case 'ok':
			content = (
				<LineageCard
					info={viewState.info}
					download={{
						isRefreshing: isRefreshingDownload,
						hasFailed: hasDownloadFailed,
						onDownload: handleDownload,
					}}
				/>
			);
			break;
		case 'not-found':
			content = (
				<div className={styles['message']}>
					<CircleX size={40} />
					<p>No encontramos este documento.</p>
					<p>
						Verifica que el enlace esté completo. Si lo escaneaste
						de una copia impresa, vuelve a escanear el código QR.
					</p>
				</div>
			);
			break;
		case 'error':
			content = (
				<div className={styles['message']}>
					<TriangleAlert size={40} />
					<p>No fue posible consultar el registro en este momento.</p>
					<p>Intenta de nuevo más tarde.</p>
				</div>
			);
			break;
	}

	return (
		<ComponentContainer>
			<div className="cyber-spacer medium"></div>
			<div className={styles['document-lineage']}>
				{viewState.status === 'ok' ? null : (
					<div className={styles['title']}>
						<div className={styles['text']}>
							<FileCheck2 /> Trazabilidad del documento
						</div>
					</div>
				)}
				{content}
			</div>
		</ComponentContainer>
	);
};

export default DocumentLineage;
